import type { Conditions } from "../../common/types.ts";
import {
	MIN_ROSTER_CHECKPOINTS,
	type MpLeagueSettings,
	ROSTER_LIMIT_CHECKPOINTS,
} from "../../common/multiplayerConfig.ts";
import * as mp from "../multiplayer/index.ts";
import { setReady } from "../multiplayer/checkpoints.ts";
import {
	cancelOffer,
	respondOffer,
	sendOfferFromCurrentTrade,
} from "../multiplayer/offers.ts";
import {
	maybeAutoResolveFa,
	placeBid,
	resolveFaRound,
	signFreeAgent,
	withdrawBid,
} from "../multiplayer/fa.ts";
import { resignPlayer } from "../multiplayer/resign.ts";
import { mpUndoTrade } from "../multiplayer/trades.ts";
import { submitMyLineup } from "../multiplayer/lineup.ts";
import {
	gatePlayMenuOption,
	mpBroadcastLivePlay,
	mpEndLiveGame,
	mpPublishIfBoundaryReached,
	mpStartFantasyDraft as startFantasyDraft,
	publishSegment,
	simToNextCheckpoint,
} from "../multiplayer/simRunner.ts";
import { joinStartedLeague, startLeague } from "../multiplayer/startLeague.ts";
import { isImportingSnapshot } from "../multiplayer/importLock.ts";
import { isMultiplayerLeague, rebuildFromServer } from "../multiplayer/sync.ts";
import { g, logEvent, toUI } from "../util/index.ts";
import { idb } from "../db/index.ts";

// All tabs re-fetch multiplayer views after any state change
const refresh = async () => {
	await toUI("realtimeUpdate", [["multiplayer"]]);
};

// ============================================================================
// Guards: features that would silently diverge league state across clients
// are disabled in multiplayer leagues until the phase that syncs them.
// ============================================================================

// Wraps the whole playMenu api namespace. In multiplayer leagues, only the
// sim runner may advance the season, only while everyone is readied up
// (sim_open), and only with options that can't overshoot the next checkpoint.
// After each action the boundary is re-checked so the sim auto-publishes the
// moment the checkpoint is reached.
export const guardPlayMenu = <T extends Record<string, any>>(
	playMenu: T,
): T => {
	const wrapped: any = {};
	for (const key of Object.keys(playMenu)) {
		wrapped[key] = async (...args: any[]) => {
			if (await isMultiplayerLeague()) {
				const blocked = await gatePlayMenuOption(key);
				if (blocked !== undefined) {
					logEvent({
						type: "error",
						text: blocked,
						saveToDb: false,
					});
					return;
				}
				const result = await playMenu[key](...args);
				// The play menu handler has fully returned, so the sim is done and
				// the gameSim lock is clear - safe to publish if we hit the
				// checkpoint (whether via day-by-day sim or a phase jump).
				await mpPublishIfBoundaryReached();
				return result;
			}
			return playMenu[key](...args);
		};
	}
	return wrapped as T;
};

// Live game sim goes through the actions namespace, not playMenu
export const guardActions = <T extends Record<string, any>>(actions: T): T => {
	const wrapped: any = { ...actions };
	for (const key of ["liveGame"]) {
		if (typeof actions[key] !== "function") {
			continue;
		}
		wrapped[key] = async (...args: any[]) => {
			if (await isMultiplayerLeague()) {
				const blocked = await gatePlayMenuOption("day");
				if (blocked !== undefined) {
					logEvent({ type: "error", text: blocked, saveToDb: false });
					return;
				}
			}
			return actions[key](...args);
		};
	}
	return wrapped as T;
};

// Tools menu skips/auto-play would jump the season without publishing
const BLOCKED_TOOLS = new Set([
	"autoPlaySeasons",
	"skipToPlayoffs",
	"skipToBeforeDraft",
	"skipToAfterDraft",
	"skipToPreseason",
]);
export const guardToolsMenu = <T extends Record<string, any>>(
	toolsMenu: T,
): T => {
	const wrapped: any = { ...toolsMenu };
	for (const key of Object.keys(toolsMenu)) {
		if (!BLOCKED_TOOLS.has(key)) {
			continue;
		}
		wrapped[key] = async (...args: any[]) => {
			if (await isMultiplayerLeague()) {
				logEvent({
					type: "error",
					text: "Season skips are disabled in multiplayer leagues - the season advances through checkpoints on the Multiplayer page.",
					saveToDb: false,
				});
				return;
			}
			return toolsMenu[key](...args);
		};
	}
	return wrapped as T;
};

// Returns an error string (the convention of the handlers it guards) when the
// current league is multiplayer.
export const mpBlockMessage = async (feature: string) => {
	if (await isMultiplayerLeague()) {
		return `${feature} is disabled in multiplayer leagues until it is synced in a later phase.`;
	}
	return undefined;
};

const mpGetSession = async () => {
	return mp.getSession();
};

const mpSignUp = async ({
	email,
	password,
}: {
	email: string;
	password: string;
}) => {
	await mp.signUp(email, password);
	await refresh();
};

const mpSignIn = async ({
	email,
	password,
}: {
	email: string;
	password: string;
}) => {
	await mp.signIn(email, password);
	await refresh();
};

const mpSignOut = async () => {
	await mp.signOut();
	await refresh();
};

// No refresh() here: the caller immediately navigates to the lobby page, and
// a concurrent home-view refresh can race with (and clobber) that navigation.
const mpCreateLobby = async (params: {
	name: string;
	displayName: string;
	settings: MpLeagueSettings;
}) => {
	return mp.createLobby(params);
};

const mpJoinLobby = async (params: {
	inviteCode: string;
	displayName: string;
}) => {
	return mp.joinLobby(params);
};

const mpLeaveLobby = async (leagueId: string) => {
	await mp.unsubscribeLobby();
	await mp.leaveLobby(leagueId);
	await refresh();
};

const mpClaimTeam = async (params: {
	leagueId: string;
	tid: number;
	confirmOutOfBand?: boolean;
}) => {
	const result = await mp.claimTeam(params);
	await refresh();
	return result;
};

const mpStartLeague = async (leagueId: string, conditions: Conditions) => {
	const result = await startLeague(leagueId, conditions);
	await refresh();
	return result;
};

const mpJoinStartedLeague = async (
	leagueId: string,
	conditions: Conditions,
) => {
	const result = await joinStartedLeague(leagueId, conditions);
	await refresh();
	return result;
};

const mpSendTradeOffer = async () => {
	const result = await sendOfferFromCurrentTrade();
	await refresh();
	return result;
};

const mpRespondTradeOffer = async ({
	offerId,
	accept,
}: {
	offerId: string;
	accept: boolean;
}) => {
	const result = await respondOffer(offerId, accept);
	await refresh();
	return result;
};

const mpCancelTradeOffer = async (offerId: string) => {
	await cancelOffer(offerId);
	await refresh();
};

const mpUndoTradeAction = async (tradeSeq: number) => {
	const error = await mpUndoTrade(tradeSeq);
	await refresh();
	return { error };
};

const mpStartFantasyDraft = async (leagueId: string) => {
	const result = await startFantasyDraft(leagueId);
	await refresh();
	return result;
};

const mpSetAutoDraft = async ({
	leagueId,
	value,
}: {
	leagueId: string;
	value: boolean;
}) => {
	const { error } = await mp
		.getClient()
		.rpc("set_auto_draft", { p_league_id: leagueId, p_value: value });
	if (error) {
		throw new Error(error.message);
	}
	await refresh();
	return { status: "ok" as const };
};

// True while a snapshot import is deleting/recreating the local league DB.
// Used by the E2E test to avoid navigating a page (killing its worker) while
// its import is mid-write - the exact interruption the self-healing rebuild
// exists to recover from.
const mpIsImporting = async () => {
	return isImportingSnapshot();
};

const mpSetReady = async ({
	leagueId,
	ready,
}: {
	leagueId: string;
	ready: boolean;
}) => {
	// Roster-size gates on ready-up: the league can't be waved forward into a
	// segment that plays games with an illegal roster (mirrors vanilla's sim
	// block). Which limit applies where is defined by ROSTER_LIMIT_CHECKPOINTS
	// (the maximum) and MIN_ROSTER_CHECKPOINTS (the minimum, enforced at fewer
	// stops because the engine tops short rosters up under spectator mode) - the
	// reasoning for each lives there.
	//
	// Through the offseason stops (lottery, draft, resign, fa) rosters are
	// legitimately mid-churn - you're holding draft picks you haven't signed,
	// carrying expiring contracts through the auction - so nothing there blocks
	// readying up. Any excess has to be cleared by the offseason stop, which is
	// the gate in front of the first games. Where the minimum does apply, the
	// point is that you fill the roster yourself, through free agency or trades,
	// instead of the engine signing minimum-salary scrubs onto your team.
	if (ready) {
		const { league: mpLeague } = await mp.getLobbyState(leagueId);

		if (ROSTER_LIMIT_CHECKPOINTS.has(mpLeague.checkpoint)) {
			const userTid = g.get("userTid");
			const maxRosterSize = g.get("maxRosterSize");
			const minRosterSize = g.get("minRosterSize");
			const myPlayers = await idb.cache.players.indexGetAll(
				"playersByTid",
				userTid,
			);
			if (myPlayers.length > maxRosterSize) {
				return {
					status: "roster_too_big" as const,
					count: myPlayers.length,
					max: maxRosterSize,
				};
			}
			if (
				MIN_ROSTER_CHECKPOINTS.has(mpLeague.checkpoint) &&
				myPlayers.length < minRosterSize
			) {
				return {
					status: "roster_too_small" as const,
					count: myPlayers.length,
					min: minRosterSize,
				};
			}
		}
	}

	// v11: readying up locks in your lineup (roster order, playing time,
	// play-through-injury) as a synced event, so the runner sims with it
	if (ready) {
		await submitMyLineup(leagueId);
	}

	const result = await setReady(leagueId, ready);
	if ((result as any).status === "fa_resolve") {
		// Everyone is done bidding; if we're the runner, resolve immediately
		// (otherwise the runner's realtime handler picks it up)
		void maybeAutoResolveFa(leagueId);
	}
	await refresh();
	return result;
};

const mpPlaceFaBid = async ({
	leagueId,
	pid,
	amount,
	years,
}: {
	leagueId: string;
	pid: number;
	amount: number;
	years: number;
}) => {
	const result = await placeBid(leagueId, pid, amount, years);
	await refresh();
	return result;
};

const mpWithdrawFaBid = async ({
	leagueId,
	pid,
}: {
	leagueId: string;
	pid: number;
}) => {
	await withdrawBid(leagueId, pid);
	await refresh();
};

const mpSignFreeAgent = async ({
	leagueId,
	pid,
	years,
}: {
	leagueId: string;
	pid: number;
	years: number;
}) => {
	const result = await signFreeAgent(leagueId, pid, years);
	await refresh();
	return result;
};

// Phase 8b: the LiveGame calls these (fire-and-forget) as the runner watches a
// game play-by-play, so spectators get a live text feed. No-op unless we're in
// a multiplayer league with a live realtime channel (broadcastToChannel).
const mpLivePlay = (arg: {
	gid: number;
	teams: Parameters<typeof mpBroadcastLivePlay>[0]["teams"];
	play: { text: string; time: string; score: [number, number] };
}) => {
	void mpBroadcastLivePlay(arg);
};

const mpLiveGameEnd = () => {
	mpEndLiveGame();
};

const mpResolveFaRound = async (leagueId: string) => {
	const result = await resolveFaRound(leagueId);
	await refresh();
	return result;
};

const mpResignPlayer = async ({
	leagueId,
	pid,
	years,
}: {
	leagueId: string;
	pid: number;
	years: number;
}) => {
	const result = await resignPlayer(leagueId, pid, years);
	await refresh();
	return result;
};

const mpOverrideFiring = async ({
	leagueId,
	userId,
}: {
	leagueId: string;
	userId: string;
}) => {
	const { error } = await mp.getClient().rpc("override_firing", {
		p_league_id: leagueId,
		p_user_id: userId,
	});
	if (error) {
		throw new Error(error.message);
	}
	await refresh();
};

const mpRebuildFromServer = async (leagueId: string) => {
	await rebuildFromServer(leagueId);
	await refresh();
};

const mpSimToNextCheckpoint = async (
	param: unknown,
	conditions: Conditions,
) => {
	const result = await simToNextCheckpoint(conditions);
	await refresh();
	return result;
};

const mpPublishSegment = async (leagueId: string) => {
	await publishSegment(leagueId);
	await refresh();
};

const mpSetSimRunner = async ({
	leagueId,
	userId,
}: {
	leagueId: string;
	userId: string;
}) => {
	const { error } = await mp.getClient().rpc("set_sim_runner", {
		p_league_id: leagueId,
		p_user_id: userId,
	});
	if (error) {
		throw new Error(error.message);
	}
	await refresh();
};

export default {
	mpGetSession,
	mpSendTradeOffer,
	mpRespondTradeOffer,
	mpCancelTradeOffer,
	mpUndoTradeAction,
	mpStartFantasyDraft,
	mpSetAutoDraft,
	mpIsImporting,
	mpSetReady,
	mpSimToNextCheckpoint,
	mpPublishSegment,
	mpSetSimRunner,
	mpPlaceFaBid,
	mpWithdrawFaBid,
	mpSignFreeAgent,
	mpLivePlay,
	mpLiveGameEnd,
	mpResolveFaRound,
	mpResignPlayer,
	mpOverrideFiring,
	mpRebuildFromServer,
	mpSignUp,
	mpSignIn,
	mpSignOut,
	mpCreateLobby,
	mpJoinLobby,
	mpLeaveLobby,
	mpClaimTeam,
	mpStartLeague,
	mpJoinStartedLeague,
};
